/**
 * ILogger.ts - ILogger - Standard interface for logging
 * 
 * CHANGE HISTORY:
 * - 251021_0452 [97a0397] danielbeer: fix: correct automated type annotations and test scripts
 * - 251020_0732 [95fb3a1] danielbeer: fix: complete test infrastructure - all tests passing
 * - 251020_0617 [9601b37] danielbeer: feat: implement interface-based architecture with enforcement
 */
export interface ILogger {
  /**
   * Log a message at specified level
   * @param level - Log level ('debug', 'info', 'warn', 'error')
   * @param message - Log message
   * @param data - Optional additional data
   */
  log(level: LogLevel, message: string, data?: Record<string, any>): void;

  /**
   * Log debug message
   * @param message - Debug message
   * @param data - Optional additional data
   */
  debug(message: string, data?: Record<string, any>): void;

  /**
   * Log info message
   * @param message - Info message
   * @param data - Optional additional data
   */
  info(message: string, data?: Record<string, any>): void;

  /**
   * Log warning message
   * @param message - Warning message
   * @param data - Optional additional data
   */
  warn(message: string, data?: Record<string, any>): void;

  /**
   * Log error message
   * @param message - Error message
   * @param data - Optional additional data
   */
  error(message: string, data?: Record<string, any>): void;
}

export type LogLevel = 'debug' | 'info' | 'warn' | 'error';

/**
 * Type guard to check if an object implements ILogger
 */
export function isLogger(obj): obj is ILogger {
  return (
    obj !== null &&
    obj !== undefined &&
    typeof obj === 'object' &&
    typeof obj.log === 'function' &&
    typeof obj.debug === 'function' &&
    typeof obj.info === 'function' &&
    typeof obj.warn === 'function' &&
    typeof obj.error === 'function'
  );
}
